import { RefObject, useEffect, useState } from 'react';

import { UseBodyScrollOptions } from '../drawer.types';

const RESIZE_DEBOUNCE_MS = 150;

export const useBodyScroll = (
  bodyRef: RefObject<HTMLDivElement>,
  { headerKeyline }: UseBodyScrollOptions,
) => {
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const body = bodyRef.current;
    if (!body) return;

    const headerEl = document.getElementById('drawer-header');
    const fadeEl = body.querySelector('#scroll-fade');
    let rafId: number | null = null;

    const updateOnScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = body;
      const hasScrolled = scrollTop > 0;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight;

      if (headerEl) {
        headerEl.classList.toggle('shadow', hasScrolled);
        if (headerKeyline === false) {
          headerEl.classList.toggle('keyline', hasScrolled);
        }
      }

      fadeEl?.classList.toggle('hidden', isAtBottom);
    };

    const handleScroll = () => {
      if (rafId != null) return;
      rafId = window.requestAnimationFrame(() => {
        updateOnScroll();
        rafId = null;
      });
    };

    body.addEventListener('scroll', handleScroll, { passive: true });
    updateOnScroll();

    return () => {
      body.removeEventListener('scroll', handleScroll);
      if (rafId != null) {
        window.cancelAnimationFrame(rafId);
      }
    };
  }, [bodyRef, headerKeyline]);

  // Check overflow on mount and whenever the window resizes (debounced).
  useEffect(() => {
    const body = bodyRef.current;
    if (!body) return;

    const checkOverflow = () => {
      setIsOverflowing(body.offsetHeight < body.scrollHeight);
    };

    checkOverflow();

    let debounceId: ReturnType<typeof setTimeout> | undefined;
    const debouncedCheck = () => {
      clearTimeout(debounceId);
      debounceId = setTimeout(checkOverflow, RESIZE_DEBOUNCE_MS);
    };

    window.addEventListener('resize', debouncedCheck);
    return () => {
      window.removeEventListener('resize', debouncedCheck);
      clearTimeout(debounceId);
    };
  }, [bodyRef]);

  return { isOverflowing };
};
